import {
  CheckIcon,
  CopyIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import type { ReactNode } from 'react';
import { isApiError, safeMessage, type ApiErrorCode } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCopyToClipboard } from './copy-button';
import { TONE_INK, type Tone } from './tone';

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="empty-state" className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      {icon && (
        <div
          aria-hidden
          className="grid size-10 place-items-center rounded-full bg-muted text-muted-foreground [&_svg]:size-5"
        >
          {icon}
        </div>
      )}
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action}
    </div>
  );
}

export interface ErrorDescription {
  title: string;
  message: string;
  code: string;
  retryable: boolean;
}

const GENERIC: ErrorDescription = {
  code: 'unknown',
  title: 'Something went wrong',
  message: 'Please try again.',
  retryable: true,
};

/** What to tell the user about a failure. Server wording is used only where it helps (validation, conflicts). */
export function describeError(error: unknown): ErrorDescription {
  if (!isApiError(error)) {
    const text = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return text.trim() ? { ...GENERIC, message: safeMessage(text.trim()) } : GENERIC;
  }
  const server = error.messages.map((m) => safeMessage(m)).filter((m) => m.trim() !== '');
  const describe = (title: string, message: string, retryable: boolean): ErrorDescription => ({
    code: error.code,
    title,
    message,
    retryable,
  });
  switch (error.code) {
    case 'validation':
      return describe("Some values weren't accepted", server.join('; ') || 'Check the values and try again.', false);
    case 'unauthorized':
      return describe('Your session has ended', 'Sign in again to continue.', false);
    case 'forbidden':
      return describe(
        "You don't have access to this",
        'Ask an administrator for the permission if you need it.',
        false,
      );
    case 'not_found':
      return describe('Not found', 'It may have been deleted, or the link is out of date.', false);
    case 'conflict':
      return describe(
        'This conflicts with existing data',
        server[0] ?? 'Another record already uses these values.',
        false,
      );
    case 'analytics_unavailable':
      return describe(
        'Analytics are unavailable',
        "The analytics warehouse isn't connected in this environment.",
        false,
      );
    case 'dev_write_blocked':
      return describe(
        'Changes are blocked in local development',
        'The local dev proxy blocks writes to shared environments. Set ALLOW_DEV_WRITES=1 to allow them, or use a local API or mocks.',
        false,
      );
    case 'contract':
      return describe(
        'The server sent something unexpected',
        "This screen couldn't read the response. If retrying doesn't help, copy the details and report it.",
        true,
      );
    case 'network':
      return describe("Can't reach the server", 'Check your connection, then try again.', true);
    case 'server':
      return describe(
        'The server had a problem',
        'Try again in a moment. If it keeps happening, copy the details and report it.',
        true,
      );
    case 'unknown':
      return describe(GENERIC.title, server[0] ?? GENERIC.message, true);
  }
}

/** For "Copy details": status, code and messages only. The raw body can hold SQL or other internals. */
function errorDetails(error: unknown): string {
  const details = isApiError(error)
    ? { status: error.status, code: error.code, messages: error.messages }
    : { status: null, code: 'unknown', messages: [describeError(error).message] };
  return JSON.stringify(details, null, 2);
}

const ACCESS: ReadonlySet<string> = new Set<ApiErrorCode>(['forbidden', 'unauthorized']);
const NOTICE: ReadonlySet<string> = new Set<ApiErrorCode>(['analytics_unavailable', 'dev_write_blocked']);

function errorLook(code: string): { tone: Tone; icon: ReactNode } {
  if (ACCESS.has(code)) return { tone: 'neutral', icon: <LockClosedIcon aria-hidden /> };
  if (code === 'not_found') return { tone: 'neutral', icon: <MagnifyingGlassIcon aria-hidden /> };
  if (NOTICE.has(code)) return { tone: 'info', icon: <InfoCircledIcon aria-hidden /> };
  return { tone: 'danger', icon: <ExclamationTriangleIcon aria-hidden /> };
}

/**
 * A failed load or action: what happened, Retry when it can help, and "Copy details" for a
 * support report. `compact` is an inline row for table bodies and cards.
 */
export function ErrorState({
  error,
  onRetry,
  title,
  compact = false,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  compact?: boolean;
  className?: string;
}) {
  const described = describeError(error);
  const { tone, icon } = errorLook(described.code);
  const { status, feedback, copy } = useCopyToClipboard();
  const size = compact ? 'xs' : 'sm';

  const actions = (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {described.retryable && onRetry && (
        <Button type="button" variant="outline" size={size} onClick={onRetry}>
          <ReloadIcon aria-hidden /> Retry
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size={size}
        className="text-muted-foreground"
        onClick={() => void copy(() => errorDetails(error))}
      >
        {status === 'copied' ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
        {status === 'copied' ? 'Copied' : 'Copy details'}
      </Button>
      <span role="status" className="sr-only">
        {feedback}
      </span>
    </div>
  );

  if (compact) {
    return (
      <div
        data-slot="error-state"
        data-compact="true"
        className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-sm', className)}
      >
        <div role="alert" className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={cn('inline-flex shrink-0 [&_svg]:size-4', TONE_INK[tone])}>{icon}</span>
          <span className="font-medium">{title ?? described.title}</span>
          <span className="text-muted-foreground">{described.message}</span>
        </div>
        {actions}
      </div>
    );
  }

  return (
    <div
      data-slot="error-state"
      className={cn('mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-12 text-center', className)}
    >
      <div role="alert" className="flex flex-col items-center gap-3">
        <div className={cn('grid size-10 place-items-center rounded-full bg-muted [&_svg]:size-5', TONE_INK[tone])}>
          {icon}
        </div>
        <p className="text-sm font-medium">{title ?? described.title}</p>
        <p className="text-sm text-muted-foreground">{described.message}</p>
      </div>
      {actions}
    </div>
  );
}
